import React, { useState } from 'react';
import { ContractButton, ContractText } from '@/components/system/ContractUI';
import { Check, X } from 'lucide-react';
import { DECISION_TYPES, REASON_CODES, REASON_LABELS } from '@/config/decisionConstants';
import { SHARED_UI_LABELS } from '@/config/task-display-config';

/**
 * DecisionFooter - Contract-Enforced Action Bar
 * Authority: Global UI Law (DETAIL_VIEW_SUPERSET_OF_CARD_VIEW)
 * 
 * Provides standardized Accept/Reject controls with mandatory reason codes for rejection.
 */
export function DecisionFooter({ onAccept, onReject, isProcessing }) {
    const [isRejecting, setIsRejecting] = useState(false);
    const [selectedReason, setSelectedReason] = useState('');
    const [rationale, setRationale] = useState('');

    const handleReject = () => {
        if (!selectedReason) return;

        // Combine code and custom text if 'OTHER'
        const finalRationale = selectedReason === REASON_CODES.OTHER
            ? `OTHER: ${rationale}`
            : selectedReason;

        onReject(finalRationale);
    };

    if (isRejecting) {
        return (
            <div className="bg-destructive/10 border-t border-destructive/20 p-4 animate-in slide-in-from-bottom-2">
                <div className="flex flex-col gap-3">
                    <ContractText variant="sectionLabel" className="text-destructive">Rejection Reason</ContractText>

                    <select
                        className="w-full p-2 rounded border border-destructive/30 text-sm bg-white focus:ring-2 focus:ring-destructive/20"
                        value={selectedReason}
                        onChange={(e) => setSelectedReason(e.target.value)}
                    >
                        <option value="">Select a reason...</option>
                        {Object.values(REASON_CODES).map(code => (
                            <option key={code} value={code}>
                                {REASON_LABELS[code]}
                            </option>
                        ))}
                    </select>

                    {selectedReason === REASON_CODES.OTHER && (
                        <textarea
                            className="w-full p-2 rounded border border-destructive/30 text-sm h-20 focus:ring-2 focus:ring-destructive/20"
                            placeholder={SHARED_UI_LABELS.reasonPlaceholder}
                            value={rationale}
                            onChange={(e) => setRationale(e.target.value)}
                        />
                    )}

                    <div className="flex gap-2 justify-end mt-2">
                        <ContractButton
                            intent="ghost"
                            size="sm"
                            onContractClick={() => setIsRejecting(false)}
                            className="text-destructive hover:bg-destructive/10"
                        >
                            Cancel
                        </ContractButton>
                        <ContractButton
                            intent="error"
                            size="sm"
                            onContractClick={handleReject}
                            disabled={!selectedReason || (selectedReason === REASON_CODES.OTHER && !rationale.trim()) || isProcessing}
                        >
                            Confirm Reject
                        </ContractButton>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white border-t border-slate-200 p-4 flex gap-3 shadow-[0_-0.25rem_0.375rem_-0.0625rem_rgba(0,0,0,0.05)]">
            <ContractButton
                intent="success"
                className="flex-1 gap-2"
                onContractClick={() => onAccept()}
                disabled={isProcessing}
            >
                <Check className="w-4 h-4" />
                Accept & Publish
            </ContractButton>

            <ContractButton
                intent="outline"
                className="flex-1 hover:border-destructive/30 hover:text-destructive gap-2"
                onContractClick={() => setIsRejecting(true)}
                disabled={isProcessing}
            >
                <X className="w-4 h-4" />
                Reject
            </ContractButton>
        </div>
    );
}
